/**
 * @file DocumentHeaderActions.tsx
 * @description Кнопки шапки карточки документа (phase19.31, часть 3; SC34,
 * SC67): главное действие и остальные видимыми кнопками, без «⋯».
 *
 * Набор и порядок даёт план (`documentActionPlan`), подписи и значки —
 * реестр `DOC_ACTION_META`. Главное — `brand` (на телефоне и планшете уходит
 * в нижнюю полосу каркаса), остальные — контуры; опасное («Аннулировать»,
 * «Удалить черновик») — последним, в опасном тоне. Главное страница кладёт
 * в `action` заголовка, остальные — строкой под шапкой с переносом (не в
 * меню и не в панель: там они обрезали номер документа на 1280).
 */

import { DOC_ACTION_META, type DocActionKey, type DocActionPlan } from "./document-actions";
import type { DocumentBusy } from "./useDocumentActions";
import { Button } from "../../ui/button";

interface ButtonProps {
  action: DocActionKey;
  busy: DocumentBusy;
  onRun: (key: DocActionKey) => void;
  primary?: boolean;
}

function DocActionButton({ action, busy, onRun, primary = false }: ButtonProps) {
  const meta = DOC_ACTION_META[action];
  const Icon = meta.icon;
  const variant = primary ? "brand" : meta.danger ? "destructive" : "outline";
  return (
    <Button variant={variant} onClick={() => onRun(action)} loading={busy === action} disabled={busy !== null && busy !== action}>
      <Icon className="size-4" aria-hidden />
      {meta.label}
    </Button>
  );
}

interface Props {
  plan: DocActionPlan;
  busy: DocumentBusy;
  onRun: (key: DocActionKey) => void;
}

/** Главная кнопка (или ничего) — в `action` заголовка. */
export function DocumentPrimaryAction({ plan, busy, onRun }: Props) {
  return plan.primary ? <DocActionButton action={plan.primary} busy={busy} onRun={onRun} primary /> : null;
}

/** Остальные кнопки — строкой под шапкой. */
export function DocumentSecondaryActions({ plan, busy, onRun }: Props) {
  if (plan.secondary.length === 0) return null;
  return (
    <>
      {plan.secondary.map((key) => (
        <DocActionButton key={key} action={key} busy={busy} onRun={onRun} />
      ))}
    </>
  );
}
